import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useCustomRouteMatch } from '../../../hooks/useCustomRouteMatch';
import { useSpecificPageLink } from './useSpecificPageLink';
import { useIsEnabledLanguageInRequests } from './useIsEnabledLanguageInRequests';
import { getMetaPageLink } from '../utils';
import { languageSelector } from '../../../redux/reducers/app/params';
import { contentAPIPrefixSelector } from '../../../redux/reducers/app/configuration';
import { SERVICE_NAME } from '../../../constants';
import { ENTITY, QUERY } from '../../../constants/content';
import { EXCLUDED_ROUTES_FOR_META_TAGS } from '../../../constants/SEOConfig';

export const useStaticMetaTagsRequestUrl = (page) => {
  const isEnabledLanguageInRequest = useIsEnabledLanguageInRequests();

  const contentAPIPrefix = useSelector(contentAPIPrefixSelector);
  const language = useSelector(languageSelector);

  return useMemo(() => {
    const metaPageLink = page ? getMetaPageLink(page) : SERVICE_NAME;

    const searchParams = new URLSearchParams({
      [QUERY.PAGE]: metaPageLink,
    });

    if (isEnabledLanguageInRequest) {
      searchParams.append(QUERY.LANGUAGE_NAME, language);
    } else {
      searchParams.append(QUERY.LANGUAGE_NULL, true);
    }

    return `${contentAPIPrefix}/${ENTITY.METATAGS}?${searchParams}`;
  }, [page, isEnabledLanguageInRequest, language, contentAPIPrefix]);
};

export const useMetaTagsRequestUrl = () => {
  const { path } = useCustomRouteMatch();

  const isEnabledLanguageInRequest = useIsEnabledLanguageInRequests();

  const contentAPIPrefix = useSelector(contentAPIPrefixSelector);
  const language = useSelector(languageSelector);

  const specificPageLink = useSpecificPageLink();

  return useMemo(() => {
    const commonPageLink = getMetaPageLink(path);

    const searchParams = new URLSearchParams({
      [QUERY.PAGE]: commonPageLink,
    });

    if (specificPageLink) {
      searchParams.append(QUERY.PAGE, specificPageLink);
    }

    if (isEnabledLanguageInRequest) {
      searchParams.append(QUERY.LANGUAGE_NAME, language);
    } else {
      searchParams.append(QUERY.LANGUAGE_NULL, true);
    }

    const isDisabled = EXCLUDED_ROUTES_FOR_META_TAGS.includes(path);

    // Return null for preventing making request
    return isDisabled
      ? null
      : `${contentAPIPrefix}/${ENTITY.METATAGS}?${searchParams}`;
  }, [
    path,
    isEnabledLanguageInRequest,
    language,
    contentAPIPrefix,
    specificPageLink,
  ]);
};
